import type { Config } from '../config.ts';
import type { SrsItem } from '../types.ts';
import { addDays } from './dates.ts';

type Box = SrsItem['box'];

/** Nowo poznane słówko: pudełko 1, pierwsza powtórka jutro. */
export function newItem(word: string, today: string, cfg: Config['srs']): SrsItem {
  return { word, box: 1, due: addDays(today, cfg.intervals[1]), lastSeen: today, correct: 0, wrong: 0 };
}

/**
 * Polityka powtórek (SPEC §23.13) — opcjonalna; bez niej działa dotychczasowa reguła (błąd = pudełko 1, pudełko 5 = stały odstęp).
 *  - `lapseDrop`: po błędzie słowo spada o tyle pudełek (min. 1) zamiast do pudełka 1: słowo, które dziecko znało miesiąc, a pomyliło raz, nie
 *    wraca do codziennych powtórek (to one zapychały kolejkę w symulacji).
 *  - `box5`: odstępy pudełka 5 przy kolejnych udanych powtórkach (np. 30, 75, 180 dni; ostatni obowiązuje dalej).
 */
export interface ReviewPolicy {
  lapseDrop?: number;
  box5?: readonly number[];
}

/** Odstęp (dni) dla słowa w danym pudełku; w pudełku 5 z polityką `box5` zależy od liczby udanych powtórek z rzędu (`laps`). */
export function scheduledInterval(box: Box, laps: number, cfg: Config['srs'], policy?: ReviewPolicy): number {
  const ladder = policy?.box5;
  if (box === 5 && ladder && ladder.length > 0) return ladder[Math.min(Math.max(0, laps), ladder.length - 1)]!;
  return cfg.intervals[box];
}

/** Aktualizacja po powtórce: dobrze = wyższe pudełko i dłuższy odstęp; źle = pudełko 1 (albo o `lapseDrop` niżej, gdy polityka to ustala). */
export function review(item: SrsItem, correct: boolean, today: string, cfg: Config['srs'], policy?: ReviewPolicy): SrsItem {
  const drop = policy?.lapseDrop;
  const box: Box = correct ? (Math.min(5, item.box + 1) as Box) : drop && drop > 0 ? (Math.max(1, item.box - drop) as Box) : 1;
  const laps = correct && item.box === 5 ? (item.laps ?? 0) + 1 : 0;
  return {
    ...item,
    box,
    due: addDays(today, scheduledInterval(box, laps, cfg, policy)),
    lastSeen: today,
    correct: item.correct + (correct ? 1 : 0),
    wrong: item.wrong + (correct ? 0 : 1),
    ...(policy?.box5 ? { laps } : {}),
  };
}

/** Zaległe słówka: najpierw najbardziej spóźnione, przy remisie niższe pudełko. */
export function dueItems(items: Record<string, SrsItem>, today: string, limit: number, exclude: ReadonlySet<string> = new Set()): SrsItem[] {
  return Object.values(items)
    .filter((it) => it.due <= today && !exclude.has(it.word))
    .sort((a, b) => (a.due === b.due ? a.box - b.box : a.due < b.due ? -1 : 1))
    .slice(0, limit);
}
